const replaceShortcodeSpansContentOnly = (
  html: string,
  shortCodeArray: {
    regex: RegExp;
    shortcode: string;
  }[]
): string => {
  return html.replace(
    /<span([^>]+)>(.*?)<\/span>/gi,
    (fullMatch, attributes, innerText) => {
      // Only process spans with sc_date or sc_intl_date class
      if (!/class=["'](sc_date|sc_intl_date)["']/i.test(attributes)) {
        return fullMatch;
      }

      // If content is already a shortcode, leave it unchanged
      if (/^\[(date|intl-date)[^\]]*\]$/i.test(innerText.trim())) {
        return fullMatch;
      }

      let newContent = innerText;
      let replaced = false;

      // Process from most specific to least specific patterns
      const orderedShortCodes = [...shortCodeArray].sort(
        (a, b) =>
          b.regex.toString().length - a.regex.toString().length ||
          b.shortcode.length - a.shortcode.length
      );

      // Try to find the first matching pattern and replace the entire content
      for (const { regex, shortcode } of orderedShortCodes) {
        const pattern = new RegExp(regex.source, regex.flags);
        if (pattern.test(innerText.trim())) {
          // Replace the entire inner text with the shortcode
          newContent = shortcode;
          replaced = true;
          break; // Stop after first match to avoid multiple replacements
        }
      }

      return replaced ? `<span${attributes}>${newContent}</span>` : fullMatch;
    }
  );
};

export const generateFinalContent = (
  components: any[],
  siteAllAvailableWidgets: any[],
  lang: string = "en"
): string => {
  // Define regex patterns in order of most specific to least specific
  const shortCodeArray =
    lang === "en"
      ? [
          {
            // Match "Month DD, YYYY" format (e.g., "July 7, 2025")
            regex:
              /\b(January|February|March|April|May|June|July|August|September|October|November|December) \d{1,2}, \d{4}\b/,
            shortcode: "[date-today format='F j, Y']",
          },
          {
            // Match "Month YYYY" format (e.g., "July 2025")
            regex:
              /\b(January|February|March|April|May|June|July|August|September|October|November|December) \d{4}\b/,
            shortcode: "[date-today format='F Y']",
          },
          {
            // Match "MMM YYYY" format (e.g., "Jul 2025")
            regex:
              /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}\b/,
            shortcode: "[date-today format='M Y']",
          },
          {
            // Match "MM/DD/YYYY" format (e.g., "07/07/2025")
            regex: /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,
            shortcode: "[date-today format='m/d/Y']",
          },
          {
            // Match "DD/MM/YYYY" format - this is tricky to distinguish from MM/DD/YYYY
            // You might need additional logic to determine the correct format
            regex: /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,
            shortcode: "[date-today format='d/m/Y']",
          },
          {
            // Match "YYYY" format (e.g., "2025")
            regex: /\b\d{4}\b/,
            shortcode: "[date-today format='Y']",
          },
        ]
      : [
          {
            // Match "Month DD, YYYY" format (e.g., "July 7, 2025")
            regex:
              /\b(January|February|March|April|May|June|July|August|September|October|November|December) \d{1,2}, \d{4}\b/,
            shortcode: "[intl-date-today format='F j, Y']",
          },
          {
            // Match "Month YYYY" format (e.g., "July 2025")
            regex:
              /\b(January|February|March|April|May|June|July|August|September|October|November|December) \d{4}\b/,
            shortcode: "[intl-date-today format='F Y']",
          },
          {
            // Match "MMM YYYY" format (e.g., "Jul 2025")
            regex:
              /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec) \d{4}\b/,
            shortcode: "[intl-date-today format='M Y']",
          },
          {
            // Match "MM/DD/YYYY" format (e.g., "07/07/2025")
            regex: /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,
            shortcode: "[intl-date-today format='m/d/Y']",
          },
          {
            // Match "DD/MM/YYYY" format
            regex: /\b\d{1,2}\/\d{1,2}\/\d{4}\b/,
            shortcode: "[intl-date-today format='d/m/Y']",
          },
          {
            // Match "YYYY" format (e.g., "2025")
            regex: /\b\d{4}\b/,
            shortcode: "[intl-date-today format='Y']",
          },
        ];

  return components
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((component) => {
      const { type, props } = component;
      if (!props) return "";

      // TEXT or SECTION
      if (type === "text" || type === "section") {
        // Skip if section has id="toc-widget"
        if (props?.id?.toLowerCase() === "toc-widget") {
          return "";
        }
        const sectionClass = props?.className || "main";
        let htmlContent = props?.text || "";

        // Replace date patterns with shortcodes (keeping all span attributes)
        htmlContent = replaceShortcodeSpansContentOnly(
          htmlContent,
          shortCodeArray
        );

        htmlContent = htmlContent.replace(
          /<(h1|h2|h3)([^>]*)>/gi,
          (match: any, tag: any, attrs: any) => {
            const randomAnchor = `anchor-${Math.floor(Math.random() * 100000)}`;
            // Avoid adding data-anchor if already present
            if (/data-anchor=/.test(attrs)) return match;
            return `<${tag}${attrs} data-anchor="${randomAnchor}">`;
          }
        );

        return `<section class="${sectionClass}">${htmlContent}</section>`;
      }

      // WIDGET COMPONENT (unchanged)
      if (type === "widgets") {
        const className = props.className || "internal-widget desktop";

        const device = className.includes("mobile")
          ? "mobile"
          : className.includes("tablet")
          ? "tablet"
          : "desktop";

        const widgetNames: string[] =
          props.attributes?.widget_name
            ?.split(",")
            .map((w: string) => w.trim()) || [];

        const widgetType = props.attributes?.widget_type;

        const matchedWidgets = siteAllAvailableWidgets.filter(
          (w: any) =>
            widgetNames.includes(w.widget_name) && w.type === widgetType
        );

        const matchedUIDs = matchedWidgets.map((w: any) => w.shared_uid);

        let shortcode = "";

        if (matchedUIDs.length === 1) {
          shortcode = `[render_widget shared_uid='${matchedUIDs[0]}' location='${device}']`;
        } else if (matchedUIDs.length > 1) {
          shortcode = `[render_widget shared_uids='${matchedUIDs
            .map((uid) => `${uid}:1`)
            .join(",")}' location='${device}']`;
        }

        return `<div class="${className}">${shortcode}</div>`;
      }

      return "";
    })
    .join("");
};
